import axios from "axios";
import jwt_decode from "jwt-decode";
import { PassLogoutProvider } from "../auth/passLogoutRef";
import { UtilProvider } from "../components/UtilContext";
import { applyAuthenticatedUserLocalePreferences } from "../components/LocaleContext";
import { resolveUiLocale } from "../utils/appUiLocale";
import { attachHttpStatus } from "../utils/serviceTaskCreateHttp";
import { API_BASE_URL, apiRequestUrl } from "../config/apiBaseUrl";
import { uiT } from "../utils/uiTranslate";

/* common variables */
export const baseURL = API_BASE_URL;
let authHeader;
let refreshTokenPromise=0;

const resolveRequestLocale = () => resolveUiLocale(PassLogoutProvider.user);

const readStoredTokens = () => {
  try {
    return JSON.parse(localStorage.getItem("tokens"));
  } catch (_e) {
    return null;
  }
};

const syncAuthHeaderFromStorage = () => {
  const tokens = readStoredTokens();
  authHeader = tokens?.access_token ? `Bearer ${tokens.access_token}` : null;
  return authHeader;
};

const forceLogout = () => {
  authHeader = null;
  localStorage.removeItem("tokens");
  if (typeof PassLogoutProvider.logout === 'function') {
    PassLogoutProvider.logout();
  }
};

const readJsonWithHttpStatus = async (response) => {
  const body = await response.json();
  return attachHttpStatus(body, response.status);
};

const waitForTokenRefresh = () => new Promise(resolve => setTimeout(resolve, 50));

const buildAuthHeaders = (includeJson = false) => {
  const headers = {
    Authorization: authHeader,
    'X-App-Locale': resolveRequestLocale(),
  };
  if (includeJson) {
    headers['Content-Type'] = 'application/json';
  }
  return headers;
};

const buildJsonApiHeaders = (includeJson = false) => ({
  ...buildAuthHeaders(includeJson),
  Accept: 'application/json',
});

const axiosToken = axios.create({
    baseURL: API_BASE_URL
});

export const refreshToken = async() => {
  refreshTokenPromise = 1;
  const authData = readStoredTokens();
  if (!authData?.access_token) {
    authHeader = null;
    refreshTokenPromise = 0;
    return;
  }
  try {
    let apiResponse = await axiosToken.get("/api/auth/refreshToken", {
      headers: { 'Authorization': `Bearer ${authData.access_token}` }
    });
    localStorage.setItem("tokens", JSON.stringify(apiResponse.data));
    authHeader = `Bearer ${apiResponse.data.access_token}`;
    let decoded_token = jwt_decode(apiResponse.data.access_token);
    const refreshedUser = decoded_token["user"];
    if (refreshedUser) {
      PassLogoutProvider.setUser(refreshedUser);
      applyAuthenticatedUserLocalePreferences(refreshedUser);
    }
  } catch (error) {
    forceLogout();
    UtilProvider.setVisible?.(false);
  } finally {
    refreshTokenPromise = 0;
  }
}

export const checkToken = async() => {
  try {
    const tokens = readStoredTokens();
    if (!tokens?.access_token) {
      authHeader = null;
      return false;
    }
    let decoded_token = jwt_decode(tokens.access_token);
    if (decoded_token.exp * 1000 < Date.now()) {
      if (!refreshTokenPromise) await refreshToken();
    }
    while (refreshTokenPromise) {
      await waitForTokenRefresh();
    }
    return true;
  } catch {
    forceLogout();
    return false;
  }
}


/* settings for GET requests to the server */ 
export const getRequest = async(component) => {
  await checkToken();
  syncAuthHeaderFromStorage();
  const tokenBefore = readStoredTokens()?.access_token;
  const response = await fetch(apiRequestUrl(component, baseURL), {
    method: "GET",
    headers: buildAuthHeaders()
  });
  // logout: netreba refreshovať
  if (component === "/api/auth/logout") {
    authHeader = null;
    return await response.json();
  }
  if (response.status === 401) {
    // ak už refresh beží, počkaj
    while (refreshTokenPromise) {
      await waitForTokenRefresh();
    }
    const tokenAfterWait = readStoredTokens()?.access_token;
    // Refresh sprav iba ak sa token nezmenil (t.j. nikto iný ho medzičasom neobnovil)
    if (tokenAfterWait === tokenBefore) {
      await refreshToken();
    }
    // ak refresh zlyhal a spravil logout, nerob retry
    if (!authHeader) {
      return await response.json();
    }
    // retry
    const retry = await fetch(apiRequestUrl(component, baseURL), {
      method: "GET",
      headers: buildAuthHeaders()
    });
    return await retry.json();
  }
  return await response.json();
};

/**
 * GET s Bearer tokenom; vráti Blob a voliteľný názov súboru z Content-Disposition.
 * Pri chybe (nie 2xx) skúsi JSON a vyhodí Error s message.
 */
export async function downloadBlobGet(component, defaultFilename = "export.xlsx") {
  await checkToken();
  syncAuthHeaderFromStorage();
  const tokenBefore = readStoredTokens()?.access_token;
  const doFetch = () =>
    fetch(apiRequestUrl(component, baseURL), {
      method: "GET",
      headers: buildAuthHeaders(),
    });

  let response = await doFetch();
  if (response.status === 401) {
    while (refreshTokenPromise) {
      await waitForTokenRefresh();
    }
    const tokenAfterWait = readStoredTokens()?.access_token;
    if (tokenAfterWait === tokenBefore) {
      await refreshToken();
    }
    if (!authHeader) {
      throw new Error("Unauthorized");
    }
    response = await doFetch();
  }
  if (!response.ok) {
    let msg = uiT('Common.ExportFailedStatus', { status: response.status });
    try {
      const j = await response.json();
      if (j?.message) msg = String(j.message);
      else if (typeof j?.detail === "string") msg = j.detail;
    } catch (_) {}
    throw new Error(msg);
  }
  const cd = response.headers.get("Content-Disposition");
  let filename = defaultFilename;
  if (cd) {
    const m = /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(cd);
    if (m) filename = decodeURIComponent(m[1].trim());
  }
  const blob = await response.blob();
  return { blob, filename };
}

/**
 * GET s Bearer tokenom; vráti text (napr. HTML tlač). Pri chybe (nie 2xx) skúsi JSON a vyhodí Error s message.
 */
export async function fetchTextGet(component) {
  await checkToken();
  syncAuthHeaderFromStorage();
  const tokenBefore = readStoredTokens()?.access_token;
  const doFetch = () =>
    fetch(apiRequestUrl(component, baseURL), {
      method: "GET",
      headers: { ...buildAuthHeaders(), Accept: "text/html, application/json;q=0.9, */*;q=0.8" },
    });

  let response = await doFetch();
  if (response.status === 401) {
    while (refreshTokenPromise) {
      await waitForTokenRefresh();
    }
    const tokenAfterWait = readStoredTokens()?.access_token;
    if (tokenAfterWait === tokenBefore) {
      await refreshToken();
    }
    if (!authHeader) {
      throw new Error("Unauthorized");
    }
    response = await doFetch();
  }
  if (!response.ok) {
    let msg = uiT('Common.RequestFailedStatus', { status: response.status });
    try {
      const j = await response.json();
      if (j?.message) msg = String(j.message);
      else if (typeof j?.detail === "string") msg = j.detail;
    } catch (_) {}
    throw new Error(msg);
  }
  const ct = (response.headers.get("Content-Type") || "").toLowerCase();
  if (ct.includes("application/json")) {
    const j = await response.json();
    const m = j?.message ? String(j.message) : uiT('Common.UnexpectedJsonResponse');
    throw new Error(m);
  }
  return await response.text();
}

export async function tabulatorGet(componentOrUrl, params = {}) {
  let component = componentOrUrl || "";
  if (typeof component === "string" && component.startsWith(baseURL)) {
    component = component.slice(baseURL.length);
  }

  const qs = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    qs.append(k, String(v));
  });

  const componentWithParams = qs.toString()
    ? `${component}${component.includes("?") ? "&" : "?"}${qs.toString()}`
    : component;

  // kľúčové: používaš existujúci getRequest => token/refresh ide rovnako ako všade inde
  return getRequest(componentWithParams);
}

/* settings for PUT requests to the server */
/* body: ak values je FormData, posielame ako multipart; inak JSON.stringify(values) */
export const putRequest = async(component, values, multipart=false) => {
  await checkToken();
  syncAuthHeaderFromStorage();
  const isFormData = values instanceof FormData;
  const body = isFormData ? values : (typeof values === 'string' ? values : JSON.stringify(values));
  const putHeaders = isFormData
    ? buildAuthHeaders()
    : buildAuthHeaders(true);
  const tokenBefore = readStoredTokens()?.access_token;
  const response = await fetch(apiRequestUrl(component, baseURL), {
    method: "PUT",
    body,
    headers: putHeaders
  });
  if (response.status === 401) {
    // ak už refresh beží, počkaj
    while (refreshTokenPromise) {
      await waitForTokenRefresh();
    }
    const tokenAfterWait = readStoredTokens()?.access_token;
    // Refresh sprav iba ak sa token nezmenil (t.j. nikto iný ho medzičasom neobnovil)
    if (tokenAfterWait === tokenBefore) {
      await refreshToken();
    }
    // ak refresh zlyhal (logout), nerob retry
    if (!authHeader) {
      return await response.json();
    }
    const retryHeaders = isFormData
      ? buildAuthHeaders()
      : buildAuthHeaders(true);

    const retry = await fetch(apiRequestUrl(component, baseURL), {
      method: "PUT",
      body,
      headers: retryHeaders
    });
    return await retry.json();
  }
  return await response.json();
}


/* settings for POST requests to the server */
/* body: ak values je FormData, posielame ako multipart; inak JSON.stringify(values) */
export const postRequest = async(component, values, multipart=false) => {
  await checkToken();

  syncAuthHeaderFromStorage();
  const isFormData = values instanceof FormData;
  const body = isFormData ? values : (typeof values === 'string' ? values : JSON.stringify(values));
  const postHeaders = isFormData
    ? buildJsonApiHeaders()
    : buildJsonApiHeaders(true);
  const tokenBefore = readStoredTokens()?.access_token;
  const response = await fetch(apiRequestUrl(component, baseURL), {
    method: "POST",
    headers: postHeaders,
    body
  });

  if (response.status === 401) {
    while (refreshTokenPromise) {
      await waitForTokenRefresh();
    }
    const tokenAfterWait = readStoredTokens()?.access_token;
    // Refresh sprav iba ak sa token nezmenil (t.j. nikto iný ho medzičasom neobnovil)
    if (tokenAfterWait === tokenBefore) {
      await refreshToken();
    }
    if (!authHeader) {
      return await readJsonWithHttpStatus(response);
    }
    const retryHeaders = isFormData
      ? buildJsonApiHeaders()
      : buildJsonApiHeaders(true);
    const retry = await fetch(apiRequestUrl(component, baseURL), {
      method: "POST",
      headers: retryHeaders,
      body
    });
    return await readJsonWithHttpStatus(retry);
  }
  return await readJsonWithHttpStatus(response);
}


/* settings for DELETE requests to the server */
export const deleteRequest = async(component) => {
  await checkToken();

  syncAuthHeaderFromStorage();
  const tokenBefore = readStoredTokens()?.access_token;
  const response = await fetch(apiRequestUrl(component, baseURL), {
    method: "DELETE",
    headers: buildAuthHeaders()
  });
  if (response.status === 401) {
    while (refreshTokenPromise) {
      await waitForTokenRefresh();
    }
    const tokenAfterWait = readStoredTokens()?.access_token;
    // Refresh sprav iba ak sa token nezmenil (t.j. nikto iný ho medzičasom neobnovil)
    if (tokenAfterWait === tokenBefore) {
      await refreshToken();
    }
    if (!authHeader) {
      return await response.json();
    }
    const retry = await fetch(apiRequestUrl(component, baseURL), {
      method: "DELETE",
      headers: buildAuthHeaders()
    });
    return await retry.json();
  }
  return await response.json();
}


/* function for detecting communication with the server */
export async function checkConnection(){
    syncAuthHeaderFromStorage();
    const response = await fetch(apiRequestUrl('/check', baseURL), {
        method: "GET",  
        headers: {
            'Authorization': authHeader
        }});
    return await response.json();
}

/* settings for POST requests to the server */ 
export const authPostRequest = async(component,values) => {
    var postHeaders;
    postHeaders ={
        'Content-Type':'application/json',
    }

    const response = await fetch(apiRequestUrl(component, baseURL), {
        method: "POST",
        headers: postHeaders,
        body: typeof values === 'string' ? values : JSON.stringify(values)
    });
    /* returning a response from the server  */
    return await response.json();
}

